import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "github-markdown-css/github-markdown.css";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import CircleIcon from "@mui/icons-material/Circle";
import "../styles/docs.css";

const DotLineMenu = () => {
  const [openIntegrations, setOpenIntegrations] = useState(false); // State for Integrations section
  const [openAPIs, setOpenAPIs] = useState(false); // State for APIs section
  const [openCICD, setOpenCICD] = useState(false); // State for CI/CD section
  const [activePath, setActivePath] = useState("");
  const location = useLocation();
  const [openGettingStarted, setOpenGettingStarted] = useState(false);
  const [openManagingTestPlans, setOpenManagingTestPlans] = useState(false);
  const [openSharingTestPlans, setOpenSharingTestPlans] = useState(false);
  const [openExecutingTestRuns, setOpenExecutingTestRuns] = useState(false);
  const [openProjectSettings, setOpenProjectSettings] = useState(false);
  const [openTestResultsAndAnalytics, setOpenTestResultsAndAnalytics] =
    useState(false);

  const [openGenerateAI, setOpenGenerateAI] = useState(false);
  const [openAutomation, setOpenAutomation] = useState(false);

  // Update activePath based on current route
  useEffect(() => {
    const path = location.pathname.split("/"); // Get last part of the path
    setActivePath(path[path.length - 1]);
    const section = path[path.length - 2];

    // Automatically expand/collapse sections based on path
    if ("integrations".includes(section)) {
      setOpenIntegrations(true);
    } else if ("apis".includes(section)) {
      setOpenAPIs(true);
    } else if ("cicd".includes(section)) {
      setOpenCICD(true);
    } else if ("getting-started".includes(section)) {
      setOpenGettingStarted(true);
    } else if ("test-plans".includes(section)) {
      setOpenManagingTestPlans(true);
    } else if ("generative-ai".includes(section)) {
      setOpenGenerateAI(true);
    } else if ("automation".includes(section)) {
      setOpenAutomation(true);
    } else if ("sharing-test-plans".includes(section)) {
      setOpenSharingTestPlans(true);
    } else if ("executing-test-runs".includes(section)) {
      setOpenExecutingTestRuns(true);
    } else if ("project-settings".includes(section)) {
      setOpenProjectSettings(true);
    } else if ("test-results-and-analytics".includes(section)) {
      setOpenTestResultsAndAnalytics(true);
    }
  }, [location.pathname]);

  const gettingStartedMap = [
    {
      link: "introduction",
      name: "Introduction",
    },
    {
      link: "interface-overview",
      name: "Interface Overview",
    },
  ];

  const integrationsMap = [
    {
      link: "integration-overview",
      name: "Integration Overview",
    },
    {
      link: "github-integration",
      name: "Github Integration",
    },
    {
      link: "jira-integration",
      name: "Jira Integration",
    },
    {
      link: "asana-integration",
      name: "Asana Integration",
    },
    {
      link: "trello-integration",
      name: "Trello Integration",
    },
  ];

  const apisMap = [
    {
      link: "api-overview",
      name: "APIs Overview",
    },
    {
      link: "auth-using-api",
      name: "API Authentication",
    },
    {
      link: "project-management",
      name: "Project Management API",
    },
    {
      link: "managing-test-suites",
      name: "Managing Test Cases",
    },
    {
      link: "test-run",
      name: "Test Run API",
    },
    {
      link: "update-test-case",
      name: "Update Test Case Status",
    },
    {
      link: "error-handling",
      name: "Error Handling",
    },
  ];

  const cicdMap = [
    {
      link: "cicd-automation",
      name: "CI/CD Automation",
    },
    {
      link: "integrating-jenkins",
      name: "Integrating Jenkins",
    },
    {
      link: "integrating-circleci",
      name: "Integrating CircleCI",
    },
    {
      link: "integrating-bitbucket",
      name: "Integrating BitBucket",
    },
    {
      link: "integrating-travisci",
      name: "Integrating Travis CI",
    },
  ];

  const managingTestPlansMap = [
    {
      link: "test-plan",
      name: "Managing Test Plans",
    },
    {
      link: "importing-test-cases",
      name: "Importing Test Cases",
    },
  ];
  const generateAiMap = [
    {
      link: "getting-started-with-ai",
      name: "Generating Test Plan",
    },
    {
      link: "ai-suggest",
      name: "Suggestion from AI",
    },
  ];
  const automationMap = [
    {
      link: "ai-automation",
      name: "Ai Automation",
    },
  ];

  const executingTestRunsMap = [
    {
      link: "customize-test-runs",
      name: "Create/Customize Test Runs",
    },
    {
      link: "updating-test-runs",
      name: "Updating Test Run Status and Details",
    },
    {
      link: "assigning-test-cases",
      name: "Manage and Track Test Run Progress",
    },
  ];

  const projectSettingsMap = [
    {
      link: "project-settings",
      name: "Project Settings",
    },
  ];
  const sharingTestPlansMap = [
    {
      link: "sharing-test-plans",
      name: "Sharing Test plans",
    },
    {
      link: "merging-projects",
      name: "Merging Projects",
    },
    {
      link: "inviting-team-members",
      name: "Inviting Team Members",
    },
  ];

  const testRunAndAnalyticsMap = [
    {
      link: "reviewing-test-results",
      name: "Reviewing test results",
    },
    {
      link: "analyzing-test-analytics",
      name: "Analyzing Test Analytics",
    },
  ];

  const handleIntegrationsClick = () => {
    setOpenIntegrations(!openIntegrations);
  };

  const handleAPIsClick = () => {
    setOpenAPIs(!openAPIs);
  };

  const handleCICDClick = () => {
    setOpenCICD(!openCICD);
  };

  const handleGettingStarted = () => {
    setOpenGettingStarted(!openGettingStarted);
  };

  const handleManagingTestPlans = () => {
    setOpenManagingTestPlans(!openManagingTestPlans);
  };

  const handleGenerateAi = () => {
    setOpenGenerateAI(!openGenerateAI);
  };

  const handleAutomation = () => {
    setOpenAutomation(!openAutomation);
  };

  const handleSharingTestPlans = () => {
    setOpenSharingTestPlans(!openSharingTestPlans);
  };
  const handleExecutingTestRuns = () => {
    setOpenExecutingTestRuns(!openExecutingTestRuns);
  };
  const handleProjectSettings = () => {
    setOpenProjectSettings(!openProjectSettings);
  };
  const handleTestResultsAndAnalytics = () => {
    setOpenTestResultsAndAnalytics(!openTestResultsAndAnalytics);
  };

  const isActive = (path) => {
    const currentPath = location.pathname.split("/").pop(); // Get last segment of path
    return currentPath === path;
  };
  return (
    <List component="nav" className="sidebar-nav overflow-auto h-full p-10">
      {/* Getting Started Section */}
      <ListItem
        button
        onClick={handleGettingStarted}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_127_168)">
              <path
                d="M6.99996 12.8334C10.2083 12.8334 12.8333 10.2084 12.8333 7.00008C12.8333 3.79175 10.2083 1.16675 6.99996 1.16675C3.79163 1.16675 1.16663 3.79175 1.16663 7.00008C1.16663 10.2084 3.79163 12.8334 6.99996 12.8334Z"
                stroke="#292D32"
                stroke-width="0.75"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M4.52087 6.99993L6.17171 8.65076L9.47921 5.34912"
                stroke="#292D32"
                stroke-width="0.75"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_127_168">
                <rect width="14" height="14" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </ListItemIcon>
        <ListItemText primary="Getting Started" />
        {openGettingStarted ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openGettingStarted} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {gettingStartedMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/getting-started/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* Integrations Section */}
      <ListItem
        button
        onClick={handleIntegrationsClick}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg
            width="13"
            height="11"
            viewBox="0 0 13 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 10.8333H2.70833V9.75H11.9167V4.33333H2.70833V3.25H13V10.8333ZM10.2917 7.58333H0V0H10.2917V1.08333H1.08333V6.5H10.2917V7.58333Z"
              fill="#292D32"
            />
          </svg>
        </ListItemIcon>
        <ListItemText primary="Integrations" />
        {openIntegrations ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openIntegrations} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {integrationsMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/integrations/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  //style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* CI/CD Automation Section */}
      <ListItem
        button
        onClick={handleCICDClick}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.0724 11.0055C12.7641 11.0064 12.4566 10.9729 12.1558 10.9055C12.3311 11.2844 12.4652 11.6812 12.5558 12.0888C12.7224 12.0888 12.8947 12.0888 13.0724 12.0888C13.639 12.0879 14.2017 11.996 14.7391 11.8166L14.2224 10.811C13.8498 10.9283 13.4629 10.9937 13.0724 11.0055Z"
              fill="black"
            />
            <path
              d="M10.2 9.92785L10 9.71674C9.56627 9.29266 9.23174 8.77785 9.02043 8.20923C8.80911 7.64061 8.72626 7.03227 8.7778 6.42785C8.83453 5.61826 9.13253 4.84424 9.63335 4.20563L9.58891 5.00007C9.58834 5.13915 9.63996 5.27338 9.73357 5.37624C9.82717 5.4791 9.95595 5.54311 10.0945 5.55563H10.1445C10.2835 5.55619 10.4178 5.50457 10.5206 5.41097C10.6235 5.31736 10.6875 5.18858 10.7 5.05007L10.8889 2.77785C10.8956 2.69738 10.8846 2.61643 10.8568 2.54063C10.8289 2.46483 10.7849 2.39601 10.7278 2.33896C10.667 2.2897 10.5965 2.25384 10.5208 2.23373C10.4452 2.21363 10.3662 2.20973 10.2889 2.22229L8.01669 2.41674C7.86934 2.42926 7.73301 2.4998 7.63768 2.61285C7.54235 2.72589 7.49583 2.87217 7.50835 3.01952C7.52088 3.16686 7.59142 3.30319 7.70446 3.39852C7.8175 3.49385 7.96379 3.54037 8.11113 3.52785L8.8278 3.46674C8.15295 4.31118 7.75521 5.34335 7.68891 6.42229C7.6315 7.16171 7.7343 7.90482 7.99029 8.60088C8.24628 9.29695 8.64944 9.92959 9.17224 10.4556L9.34446 10.6612C10.0544 11.3948 10.4628 12.3684 10.4889 13.389C10.5185 13.8512 10.4697 14.3152 10.3445 14.7612L11.3445 15.2667C11.5596 14.6405 11.6447 13.977 11.5945 13.3167C11.5604 12.0545 11.0642 10.8486 10.2 9.92785Z"
              fill="black"
            />
            <path
              d="M18.0002 9.94999L17.328 9.99999C17.9414 9.20608 18.3197 8.25598 18.4199 7.25773C18.5201 6.25947 18.3382 5.25313 17.8949 4.35313C17.4515 3.45313 16.7646 2.69559 15.912 2.16666C15.0595 1.63773 14.0757 1.35863 13.0724 1.3611C12.5057 1.36012 11.9427 1.45208 11.4058 1.63332L11.9113 2.63332C12.5456 2.4537 13.2128 2.42368 13.8606 2.54561C14.5084 2.66754 15.1191 2.93811 15.6447 3.33603C16.1702 3.73395 16.5962 4.24837 16.8893 4.83883C17.1824 5.42929 17.3344 6.07969 17.3335 6.73888C17.3334 7.69147 17.0102 8.61586 16.4169 9.3611L16.4724 8.50554C16.4772 8.43259 16.4675 8.35941 16.444 8.29019C16.4204 8.22098 16.3835 8.15707 16.3352 8.10213C16.287 8.04719 16.2284 8.00229 16.1628 7.96999C16.0972 7.93769 16.0259 7.91862 15.953 7.91388C15.88 7.90914 15.8069 7.91881 15.7376 7.94235C15.6684 7.96589 15.6045 8.00283 15.5496 8.05106C15.4946 8.0993 15.4497 8.15788 15.4174 8.22347C15.3851 8.28906 15.3661 8.36037 15.3613 8.43332L15.2058 10.7C15.2014 10.7804 15.2145 10.8608 15.2442 10.9356C15.2739 11.0104 15.3196 11.0779 15.378 11.1333C15.4279 11.184 15.4877 11.2239 15.5536 11.2507C15.6195 11.2774 15.6902 11.2904 15.7613 11.2889H15.8169L18.0835 11.0555C18.1566 11.0486 18.2276 11.0272 18.2924 10.9927C18.3572 10.9581 18.4145 10.9111 18.461 10.8543C18.5075 10.7974 18.5423 10.732 18.5633 10.6616C18.5843 10.5913 18.5912 10.5175 18.5835 10.4444C18.5681 10.303 18.499 10.1729 18.3905 10.0809C18.2819 9.98888 18.1423 9.94204 18.0002 9.94999Z"
              fill="black"
            />
            <path
              d="M2.26119 11.3554H2.30564L4.57786 11.161C4.65082 11.1548 4.72184 11.1343 4.78687 11.1006C4.8519 11.067 4.90966 11.0209 4.95687 10.9649C5.00407 10.9089 5.03979 10.8442 5.06198 10.7744C5.08417 10.7046 5.0924 10.6312 5.08619 10.5582C5.07999 10.4853 5.05948 10.4142 5.02583 10.3492C4.99219 10.2842 4.94606 10.2264 4.89008 10.1792C4.83411 10.132 4.76939 10.0963 4.69961 10.0741C4.62984 10.0519 4.55637 10.0437 4.48342 10.0499L3.71119 10.1165C4.43857 9.57995 5.31842 9.28991 6.22231 9.28877C6.52681 9.2906 6.8303 9.32411 7.12786 9.38877C6.95336 9.00999 6.82108 8.61316 6.73342 8.20544C6.56675 8.20544 6.39453 8.20544 6.22231 8.20544C5.03699 8.20367 3.88444 8.59436 2.94453 9.31655L3.01119 8.58877C3.01727 8.51577 3.00885 8.44228 2.98641 8.37255C2.96398 8.30281 2.92797 8.2382 2.88047 8.18244C2.83296 8.12667 2.7749 8.08085 2.70962 8.04761C2.64433 8.01438 2.57312 7.99438 2.50008 7.98877C2.35352 7.97645 2.20806 8.02275 2.09558 8.11752C1.98311 8.21229 1.9128 8.3478 1.90008 8.49432L1.70564 10.761C1.69949 10.8376 1.7093 10.9146 1.73446 10.9873C1.75961 11.0599 1.79955 11.1265 1.85177 11.1829C1.90398 11.2393 1.96733 11.2842 2.0378 11.3149C2.10827 11.3455 2.18434 11.3612 2.26119 11.361V11.3554Z"
              fill="black"
            />
            <path
              d="M10.2334 15.6832L7.96121 15.8332C7.81387 15.8428 7.67636 15.9105 7.57895 16.0215C7.48153 16.1324 7.43219 16.2776 7.44176 16.4249C7.45134 16.5722 7.51906 16.7097 7.63002 16.8072C7.74098 16.9046 7.88609 16.9539 8.03343 16.9443L8.88899 16.8832C8.25981 17.3889 7.50054 17.7065 6.6987 17.7994C5.89686 17.8923 5.08508 17.7568 4.35693 17.4084C3.62878 17.06 3.01389 16.5129 2.58315 15.8303C2.1524 15.1476 1.92332 14.3571 1.92232 13.5499C1.92632 13.174 1.9805 12.8003 2.08343 12.4388L1.11121 11.9221C0.9391 12.4514 0.850995 13.0044 0.850098 13.561C0.856616 14.5671 1.14468 15.5513 1.68166 16.4022C2.21865 17.2531 2.98312 17.9366 3.88853 18.3754C4.79393 18.8143 5.80411 18.9908 6.80469 18.8852C7.80527 18.7796 8.7563 18.3959 9.5501 17.7777L9.48343 18.4332C9.47573 18.5058 9.48242 18.5792 9.5031 18.6492C9.52379 18.7193 9.55807 18.7845 9.60399 18.8413C9.64991 18.898 9.70657 18.9452 9.77072 18.98C9.83488 19.0149 9.90527 19.0367 9.97787 19.0443H10.039C10.1772 19.045 10.3107 18.9942 10.4134 18.9017C10.5161 18.8093 10.5807 18.6819 10.5945 18.5443L10.8279 16.2888C10.8361 16.2084 10.8267 16.1272 10.8004 16.0508C10.774 15.9744 10.7313 15.9047 10.6753 15.8466C10.6192 15.7884 10.5511 15.7431 10.4758 15.714C10.4004 15.6848 10.3196 15.6724 10.239 15.6777L10.2334 15.6832Z"
              fill="black"
            />
          </svg>
        </ListItemIcon>
        <ListItemText primary="CI/CD Automation" />
        {openCICD ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openCICD} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {cicdMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/cicd/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* APIs Section */}
      <ListItem
        button
        onClick={handleAPIsClick}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_287_254)">
              <path d="M15 0H0V15H15V0Z" fill="white" fill-opacity="0.01" />
              <path
                d="M5.625 7.48038V3.125C5.625 2.08947 6.46447 1.25 7.5 1.25C8.53553 1.25 9.375 2.08947 9.375 3.125V3.75178"
                stroke="black"
                stroke-linecap="round"
              />
              <path
                d="M9.375 7.50098V11.8749C9.375 12.9104 8.53553 13.7499 7.5 13.7499C6.46447 13.7499 5.625 12.9104 5.625 11.8749V11.2405"
                stroke="black"
                stroke-linecap="round"
              />
              <path
                d="M7.5 9.375H3.12005C2.08725 9.375 1.25 8.53553 1.25 7.5C1.25 6.46447 2.08725 5.625 3.12005 5.625H3.74644"
                stroke="black"
                stroke-linecap="round"
              />
              <path
                d="M7.5 5.625H11.8715C12.909 5.625 13.75 6.46447 13.75 7.5C13.75 8.53553 12.909 9.375 11.8715 9.375H11.2707"
                stroke="black"
                stroke-linecap="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_287_254">
                <rect width="15" height="15" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </ListItemIcon>
        <ListItemText primary="APIs" />
        {openAPIs ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openAPIs} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {apisMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/apis/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* Generate AI */}
      <ListItem
        button
        onClick={handleGenerateAi}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 12 13" fill="none">
<path d="M5.65168 2.92305C4.97766 2.24878 4.51631 1.07133 4.28736 0C4.05806 1.07155 3.59721 2.24924 2.92294 2.92351C2.24891 3.59732 1.07111 4.05866 0 4.28807C1.07155 4.51702 2.24913 4.97809 2.92316 5.65239C3.59718 6.32641 4.05852 7.50411 4.28783 8.57544C4.51678 7.50389 4.97798 6.3262 5.6519 5.65193C6.32592 4.9779 7.50373 4.51678 8.57484 4.28736C7.50353 4.05841 6.32595 3.5971 5.65168 2.92305Z" fill="#262A39"/>
<path d="M10.0299 8.32172C9.62761 7.91919 9.35154 7.21539 9.21477 6.57544C9.07775 7.21539 8.80262 7.91917 8.39985 8.32194C7.99707 8.72427 7.29341 9.00009 6.65356 9.13711C7.29363 9.2741 7.99707 9.54948 8.39985 9.95226C8.80262 10.3548 9.07824 11.0586 9.21524 11.6985C9.35198 11.0583 9.62761 10.3548 10.0301 9.95204C10.4327 9.54948 11.1365 9.27386 11.7762 9.13665C11.1365 8.99987 10.4327 8.72427 10.0299 8.32172Z" fill="#262A39"/>
<path d="M2.88984 8.69849C2.7748 9.23574 2.5438 9.82677 2.20573 10.1648C1.8677 10.5026 1.27691 10.7341 0.739746 10.8492C1.27691 10.9644 1.8677 11.1952 2.20573 11.5335C2.5438 11.8715 2.77527 12.4621 2.8903 12.9996C3.00523 12.4621 3.23634 11.8715 3.57453 11.5333C3.91233 11.1952 4.50323 10.964 5.04029 10.8487C4.50326 10.7339 3.91233 10.5027 3.57431 10.1646C3.23634 9.8268 3.00477 9.23576 2.88984 8.69849Z" fill="#262A39"/>
</svg>
        </ListItemIcon>
        <ListItemText primary="Generate AI" />
        {openGenerateAI ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openGenerateAI} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {generateAiMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/generate-ai/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* AI Automation */}
      <ListItem
        button
        onClick={handleAutomation}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 49 38" fill="none" class="__web-inspector-hide-shortcut__">
<path fill-rule="evenodd" clip-rule="evenodd" d="M27.9567 22.8539C28.399 22.7742 28.8475 22.7342 29.2969 22.7344C30.2419 22.7344 31.1461 22.9092 31.9791 23.2282L35.2931 18.5156L35.4197 18.5873C34.1911 17.2145 33.513 15.4361 33.5156 13.5938C33.5156 9.45164 36.8735 6.09375 41.0156 6.09375C45.1577 6.09375 48.5156 9.45164 48.5156 13.5938C48.5156 17.7359 45.1577 21.0938 41.0156 21.0938C40.155 21.0938 39.3281 20.9487 38.5582 20.682L35.2043 25.6127C36.2386 26.931 36.7995 28.5588 36.7969 30.2344C36.7969 34.3765 33.439 37.7344 29.2969 37.7344C25.1548 37.7344 21.7969 34.3765 21.7969 30.2344C21.7969 27.9452 22.8225 25.8956 24.4392 24.5198L19.5727 14.8448C19.0707 14.9483 18.5594 15.0003 18.0469 15C17.4014 15.0005 16.7587 14.9178 16.1344 14.7539L12.6853 20.8313C14.1117 22.1965 15 24.1195 15 26.25C15 30.3921 11.6421 33.75 7.5 33.75C3.35789 33.75 0 30.3921 0 26.25C0 22.1079 3.35789 18.75 7.5 18.75C8.12227 18.75 8.72672 18.8257 9.30469 18.9684L12.7755 12.8348C11.3995 11.4757 10.5469 9.58758 10.5469 7.5C10.5469 3.35789 13.9048 0 18.0469 0C22.189 0 25.5469 3.35789 25.5469 7.5C25.5469 9.74133 24.5637 11.753 23.0053 13.1273L27.9567 22.8539ZM41.0156 17.3438C43.0868 17.3438 44.7656 15.6649 44.7656 13.5938C44.7656 11.5226 43.0868 9.84375 41.0156 9.84375C38.9445 9.84375 37.2656 11.5226 37.2656 13.5938C37.2656 15.6649 38.9445 17.3438 41.0156 17.3438ZM7.5 30C9.57117 30 11.25 28.3212 11.25 26.25C11.25 24.1788 9.57117 22.5 7.5 22.5C5.42883 22.5 3.75 24.1788 3.75 26.25C3.75 28.3212 5.42883 30 7.5 30ZM18.0469 11.25C20.118 11.25 21.7969 9.57117 21.7969 7.5C21.7969 5.42883 20.118 3.75 18.0469 3.75C15.9757 3.75 14.2969 5.42883 14.2969 7.5C14.2969 9.57117 15.9757 11.25 18.0469 11.25ZM29.2969 33.9844C31.368 33.9844 33.0469 32.3055 33.0469 30.2344C33.0469 28.1632 31.368 26.4844 29.2969 26.4844C27.2257 26.4844 25.5469 28.1632 25.5469 30.2344C25.5469 32.3055 27.2257 33.9844 29.2969 33.9844Z" fill="#262A39"/>

          <path
            xmlns="http://www.w3.org/2000/svg"
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M27.9567 22.8539C28.399 22.7742 28.8475 22.7342 29.2969 22.7344C30.2419 22.7344 31.1461 22.9092 31.9791 23.2282L35.2931 18.5156L35.4197 18.5873C34.1911 17.2145 33.513 15.4361 33.5156 13.5938C33.5156 9.45164 36.8735 6.09375 41.0156 6.09375C45.1577 6.09375 48.5156 9.45164 48.5156 13.5938C48.5156 17.7359 45.1577 21.0938 41.0156 21.0938C40.155 21.0938 39.3281 20.9487 38.5582 20.682L35.2043 25.6127C36.2386 26.931 36.7995 28.5588 36.7969 30.2344C36.7969 34.3765 33.439 37.7344 29.2969 37.7344C25.1548 37.7344 21.7969 34.3765 21.7969 30.2344C21.7969 27.9452 22.8225 25.8956 24.4392 24.5198L19.5727 14.8448C19.0707 14.9483 18.5594 15.0003 18.0469 15C17.4014 15.0005 16.7587 14.9178 16.1344 14.7539L12.6853 20.8313C14.1117 22.1965 15 24.1195 15 26.25C15 30.3921 11.6421 33.75 7.5 33.75C3.35789 33.75 0 30.3921 0 26.25C0 22.1079 3.35789 18.75 7.5 18.75C8.12227 18.75 8.72672 18.8257 9.30469 18.9684L12.7755 12.8348C11.3995 11.4757 10.5469 9.58758 10.5469 7.5C10.5469 3.35789 13.9048 0 18.0469 0C22.189 0 25.5469 3.35789 25.5469 7.5C25.5469 9.74133 24.5637 11.753 23.0053 13.1273L27.9567 22.8539ZM41.0156 17.3438C43.0868 17.3438 44.7656 15.6649 44.7656 13.5938C44.7656 11.5226 43.0868 9.84375 41.0156 9.84375C38.9445 9.84375 37.2656 11.5226 37.2656 13.5938C37.2656 15.6649 38.9445 17.3438 41.0156 17.3438ZM7.5 30C9.57117 30 11.25 28.3212 11.25 26.25C11.25 24.1788 9.57117 22.5 7.5 22.5C5.42883 22.5 3.75 24.1788 3.75 26.25C3.75 28.3212 5.42883 30 7.5 30ZM18.0469 11.25C20.118 11.25 21.7969 9.57117 21.7969 7.5C21.7969 5.42883 20.118 3.75 18.0469 3.75C15.9757 3.75 14.2969 5.42883 14.2969 7.5C14.2969 9.57117 15.9757 11.25 18.0469 11.25ZM29.2969 33.9844C31.368 33.9844 33.0469 32.3055 33.0469 30.2344C33.0469 28.1632 31.368 26.4844 29.2969 26.4844C27.2257 26.4844 25.5469 28.1632 25.5469 30.2344C25.5469 32.3055 27.2257 33.9844 29.2969 33.9844Z"
            fill="#262A39"
          />
          </svg>
        </ListItemIcon>
        <ListItemText primary="Automation" />
        {openAutomation ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openAutomation} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {automationMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/automation/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* Managing Test Plans Section */}
      <ListItem
        button
        onClick={handleManagingTestPlans}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg
            width="11"
            height="10"
            viewBox="0 0 11 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.75 1.33438V0.21875C1.75 0.098 1.652 0 1.53125 0C1.4105 0 1.3125 0.098 1.3125 0.21875V1.33438C0.571375 1.44156 0 2.07288 0 2.84375C0 3.61462 0.571375 4.24594 1.3125 4.35313V9.40625C1.3125 9.527 1.4105 9.625 1.53125 9.625C1.652 9.625 1.75 9.527 1.75 9.40625V4.35313C2.49113 4.24594 3.0625 3.61462 3.0625 2.84375C3.0625 2.07288 2.49113 1.44156 1.75 1.33438ZM1.53125 3.9375C0.928375 3.9375 0.4375 3.44662 0.4375 2.84375C0.4375 2.24044 0.928375 1.75 1.53125 1.75C2.13412 1.75 2.625 2.24044 2.625 2.84375C2.625 3.44662 2.13412 3.9375 1.53125 3.9375ZM5.6875 5.27187V0.21875C5.6875 0.098 5.5895 0 5.46875 0C5.348 0 5.25 0.098 5.25 0.21875V5.27187C4.50887 5.37906 3.9375 6.01038 3.9375 6.78125C3.9375 7.55212 4.50887 8.18344 5.25 8.29063V9.40625C5.25 9.527 5.348 9.625 5.46875 9.625C5.5895 9.625 5.6875 9.527 5.6875 9.40625V8.29063C6.42863 8.18344 7 7.55212 7 6.78125C7 6.01038 6.42863 5.37906 5.6875 5.27187ZM5.46875 7.875C4.86587 7.875 4.375 7.38413 4.375 6.78125C4.375 6.17794 4.86587 5.6875 5.46875 5.6875C6.07163 5.6875 6.5625 6.17794 6.5625 6.78125C6.5625 7.38413 6.07163 7.875 5.46875 7.875ZM10.9375 4.15625C10.9375 3.38538 10.3661 2.75406 9.625 2.64688V0.21875C9.625 0.098 9.527 0 9.40625 0C9.2855 0 9.1875 0.098 9.1875 0.21875V2.64688C8.44637 2.75406 7.875 3.38538 7.875 4.15625C7.875 4.92712 8.44637 5.55844 9.1875 5.66563V9.40625C9.1875 9.527 9.2855 9.625 9.40625 9.625C9.527 9.625 9.625 9.527 9.625 9.40625V5.66563C10.3661 5.55844 10.9375 4.92712 10.9375 4.15625ZM9.40625 5.25C8.80338 5.25 8.3125 4.75913 8.3125 4.15625C8.3125 3.55294 8.80338 3.0625 9.40625 3.0625C10.0091 3.0625 10.5 3.55294 10.5 4.15625C10.5 4.75913 10.0091 5.25 9.40625 5.25Z"
              fill="#0B1719"
            />
          </svg>
        </ListItemIcon>
        <ListItemText primary="Test Plans" />
        {openManagingTestPlans ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openManagingTestPlans} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {managingTestPlansMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/test-plans/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* Executing Test Runs Section */}
      <ListItem
        button
        onClick={handleExecutingTestRuns}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.25 1V2.75M6.25 11.5V9.75001M1.70337 8.87501L3.21891 8.00001M11.5 6.25001H9.75M8.875 10.7966L8 9.28107M1 6.25001H2.75M3.625 1.70337L4.5 3.21891M3.625 10.7966L4.5 9.28107M10.7966 8.87501L9.28112 8.00001M1.70337 3.625L3.21891 4.5"
              stroke="black"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </ListItemIcon>
        <ListItemText primary="Execute Test Runs" />
        {openExecutingTestRuns ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openExecutingTestRuns} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {executingTestRunsMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/executing-test-runs/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* Test results and Analytics Section */}
      <ListItem
        button
        onClick={handleTestResultsAndAnalytics}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg
            width="14"
            height="13"
            viewBox="0 0 14 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.4445 0V10.1093H8.55558V0H5.4445ZM7.70005 0.855402H6.30005V9.25391H7.70005V0.855402ZM0.777832 3.11055H3.88894V10.1093H0.777832V3.11055ZM1.63339 3.96596H3.03339V9.25391H1.63339V3.96596Z"
              fill="black"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M14 11.6646H0V12.5199H14V11.6646Z"
              fill="black"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.1111 4.66553H13.2221V10.109H10.1111V4.66553ZM10.9666 5.52093H12.3666V9.2536H10.9666V5.52093Z"
              fill="black"
            />
          </svg>
        </ListItemIcon>
        <ListItemText primary="Test Results & Analytics" />
        {openTestResultsAndAnalytics ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openTestResultsAndAnalytics} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {testRunAndAnalyticsMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/test-results-and-analytics/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* Share and Collab Section */}
      <ListItem
        button
        onClick={handleSharingTestPlans}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg
            width="16"
            height="15"
            viewBox="0 0 16 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.99996 7.5C5.99996 8.36294 5.25377 9.0625 4.33329 9.0625C3.41282 9.0625 2.66663 8.36294 2.66663 7.5C2.66663 6.63706 3.41282 5.9375 4.33329 5.9375C5.25377 5.9375 5.99996 6.63706 5.99996 7.5Z"
              stroke="#1C274C"
              stroke-width="0.75"
            />
            <path
              d="M9.33333 4.0625L6 6.25"
              stroke="#1C274C"
              stroke-width="0.75"
              stroke-linecap="round"
            />
            <path
              d="M9.33333 10.9375L6 8.75"
              stroke="#1C274C"
              stroke-width="0.75"
              stroke-linecap="round"
            />
            <path
              d="M12.6667 11.5625C12.6667 12.4254 11.9205 13.125 11 13.125C10.0796 13.125 9.33337 12.4254 9.33337 11.5625C9.33337 10.6996 10.0796 10 11 10C11.9205 10 12.6667 10.6996 12.6667 11.5625Z"
              stroke="#1C274C"
              stroke-width="0.75"
            />
            <path
              d="M12.6667 3.4375C12.6667 4.30044 11.9205 5 11 5C10.0796 5 9.33337 4.30044 9.33337 3.4375C9.33337 2.57456 10.0796 1.875 11 1.875C11.9205 1.875 12.6667 2.57456 12.6667 3.4375Z"
              stroke="#1C274C"
              stroke-width="0.75"
            />
          </svg>
        </ListItemIcon>
        <ListItemText primary="Share and Collab" />
        {openSharingTestPlans ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openSharingTestPlans} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {sharingTestPlansMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/sharing-test-plans/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      {/* Project Settings Section */}
      <ListItem
        button
        onClick={handleProjectSettings}
        style={{
          height: "45px",
        }}
      >
        <ListItemIcon>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_277_313)">
              <path
                d="M7 8.75C7.9665 8.75 8.75 7.9665 8.75 7C8.75 6.0335 7.9665 5.25 7 5.25C6.0335 5.25 5.25 6.0335 5.25 7C5.25 7.9665 6.0335 8.75 7 8.75Z"
                stroke="#1C274C"
              />
              <path
                d="M8.02986 1.25555C7.81542 1.16675 7.54365 1.16675 7.00004 1.16675C6.45643 1.16675 6.18466 1.16675 5.97022 1.25555C5.68437 1.37397 5.45725 1.60109 5.33884 1.88695C5.28479 2.01745 5.26363 2.16921 5.25535 2.39058C5.24319 2.71589 5.07636 3.01702 4.79442 3.17979C4.5125 3.34256 4.1683 3.33648 3.88048 3.18436C3.68463 3.08084 3.54263 3.02328 3.40259 3.00484C3.09581 2.96445 2.78556 3.04758 2.54008 3.23595C2.35597 3.37722 2.22007 3.61261 1.94827 4.08337C1.67647 4.55415 1.54058 4.78953 1.51028 5.01961C1.4699 5.32638 1.55303 5.63663 1.74139 5.88212C1.82737 5.99418 1.9482 6.08833 2.13573 6.20617C2.41142 6.37942 2.58881 6.67452 2.58879 7.00008C2.58877 7.32564 2.41139 7.62069 2.13573 7.79388C1.94817 7.91177 1.82732 8.00598 1.74134 8.11804C1.55297 8.36351 1.46984 8.67372 1.51023 8.9805C1.54052 9.21057 1.67642 9.446 1.94821 9.91675C2.22002 10.3875 2.35592 10.6229 2.54002 10.7642C2.7855 10.9525 3.09575 11.0356 3.40253 10.9953C3.54256 10.9768 3.68456 10.9193 3.8804 10.8158C4.16823 10.6636 4.51245 10.6576 4.7944 10.8203C5.07635 10.9831 5.24319 11.2843 5.25535 11.6096C5.26364 11.831 5.28479 11.9827 5.33884 12.1132C5.45725 12.3991 5.68437 12.6262 5.97022 12.7446C6.18466 12.8334 6.45643 12.8334 7.00004 12.8334C7.54365 12.8334 7.81542 12.8334 8.02986 12.7446C8.31569 12.6262 8.54284 12.3991 8.6612 12.1132C8.71527 11.9827 8.73645 11.831 8.74473 11.6096C8.75686 11.2843 8.9237 10.9831 9.20562 10.8203C9.48755 10.6575 9.83177 10.6636 10.1196 10.8158C10.3155 10.9193 10.4575 10.9768 10.5975 10.9952C10.9042 11.0356 11.2145 10.9525 11.46 10.7642C11.6441 10.6229 11.78 10.3875 12.0518 9.91669C12.3236 9.44594 12.4595 9.21057 12.4898 8.9805C12.5302 8.67372 12.447 8.36345 12.2587 8.11798C12.1727 8.00592 12.0518 7.91172 11.8643 7.79388C11.5887 7.62069 11.4113 7.32558 11.4113 7.00002C11.4113 6.67447 11.5887 6.37947 11.8643 6.20628C12.0519 6.08839 12.1727 5.99424 12.2587 5.88212C12.4471 5.63667 12.5302 5.32642 12.4898 5.01965C12.4596 4.78957 12.3237 4.55418 12.0518 4.08341C11.78 3.61265 11.6441 3.37726 11.46 3.23599C11.2146 3.04762 10.9043 2.96449 10.5975 3.00488C10.4575 3.02332 10.3155 3.08087 10.1196 3.18438C9.83183 3.33651 9.48761 3.34259 9.20568 3.17981C8.9237 3.01703 8.75686 2.71588 8.74473 2.39055C8.73645 2.16919 8.71527 2.01744 8.6612 1.88695C8.54284 1.60109 8.31569 1.37397 8.02986 1.25555Z"
                stroke="#1C274C"
              />
            </g>
            <defs>
              <clipPath id="clip0_277_313">
                <rect width="14" height="14" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </ListItemIcon>
        <ListItemText primary="Project Settings" />
        {openProjectSettings ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={openProjectSettings} timeout="auto" unmountOnExit>
        <div style={{ position: "relative" }}>
          {/* Timeline Line */}
          <svg
            style={{
              position: "absolute",
              top: "20px",
              left: "43px",
              width: "2px",
              height: "calc(100% - 40px)", // Adjust this to match the height of the list
              zIndex: -10,
            }}
            viewBox="0 0 2 100%"
            preserveAspectRatio="none"
          >
            <line
              x1="1"
              y1="0"
              x2="1"
              y2="100%"
              stroke="#E4E4E4"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>

          <List component="div" disablePadding>
            {projectSettingsMap.map((item, ind) => (
              <ListItem
                button
                component={Link}
                to={`/docs/project-settings/${item.link}`}
                className={`nested-list-item ${
                  isActive(item.link) ? "active" : ""
                }`}
                style={{
                  position: "relative",
                  zIndex: 1,
                  height: "auto",
                  paddingLeft: "40px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText
                  style={{ textWrap: "balance" }}
                  primary={item.name}
                />
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>
    </List>
  );
};

export default DotLineMenu;
